import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import type { Entity } from '@backstage/catalog-model';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { usePipelineStatus, statusToColor, statusToLabel } from '../../hooks';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  section: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
}));

interface EnvironmentInfoProps {
  entity?: Entity | null;
}

interface WorkflowConfig {
  action: string;
  additional_params?: string[];
}

interface EnvironmentMetadata {
  type?: string;
  domain_name?: string;
  workflow?: WorkflowConfig;
}

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const EnvironmentInfoContent = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const { status, isTriggering, runUrl } = usePipelineStatus(entity);

  const envMetadataRaw = entity.metadata.environment;
  const envMetadata: EnvironmentMetadata = isObject(envMetadataRaw)
    ? (envMetadataRaw as EnvironmentMetadata)
    : {};
  const workflow = envMetadata.workflow;

  const chipLabel = statusToLabel(status);
  const chipColor = statusToColor(status);

  return (
    <Card>
      <CardContent>
        <Box className={classes.header}>
          <Box>
            <Typography variant="h6" gutterBottom>
              <EntityRefLink entityRef={entity} defaultKind="Component">
                {entity.metadata.title || entity.metadata.name}
              </EntityRefLink>
            </Typography>

            {entity.metadata.description && (
              <Typography variant="body2" color="textSecondary" paragraph>
                {entity.metadata.description}
              </Typography>
            )}
          </Box>

          <Box style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Typography variant="subtitle2" className={classes.label}>
              Status
            </Typography>
            {isTriggering ? (
              <CircularProgress size={20} />
            ) : (
              <Tooltip title={runUrl ? `View run: ${runUrl}` : ''} placement="left">
                <Chip label={chipLabel} color={chipColor} />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider />

        {envMetadata.type && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              Environment Type
            </Typography>
            <Chip label={String(envMetadata.type)} color="primary" />
          </Box>
        )}

        {entity.spec?.lifecycle && typeof entity.spec.lifecycle === 'string' && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              Lifecycle
            </Typography>
            <Chip label={String(entity.spec.lifecycle)} size="small" />
          </Box>
        )}

        {entity.spec?.owner && typeof entity.spec.owner === 'string' && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              Owner
            </Typography>
            <Typography variant="body1">{String(entity.spec.owner)}</Typography>
          </Box>
        )}

        {workflow && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              Workflow
            </Typography>
            <Typography variant="body2">
              <strong>Action:</strong> {String(workflow.action)}
            </Typography>
            {workflow.additional_params && workflow.additional_params.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2">
                  <strong>Parameters:</strong>
                </Typography>
                <Box className={classes.chipContainer}>
                  {workflow.additional_params.map((param: string) => (
                    <Chip key={param} label={param} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {entity.metadata.annotations?.['github.com/project-slug'] && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              GitHub Project
            </Typography>
            <Typography variant="body2">
              {String(entity.metadata.annotations['github.com/project-slug'])}
            </Typography>
          </Box>
        )}

        {envMetadata.domain_name && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              Domain
            </Typography>
            <Typography variant="body1">
              <EntityRefLink
                entityRef={`domain:${String(envMetadata.domain_name)}`}
                defaultKind="Domain"
              >
                {String(envMetadata.domain_name)}
              </EntityRefLink>
            </Typography>
          </Box>
        )}

        {entity.spec?.system && typeof entity.spec.system === 'string' && (
          <Box className={classes.section}>
            <Typography variant="subtitle1" className={classes.label}>
              System
            </Typography>
            <Typography variant="body1">
              <EntityRefLink
                entityRef={`system:${String(entity.spec.system)}`}
                defaultKind="System"
              >
                {String(entity.spec.system)}
              </EntityRefLink>
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const EnvironmentInfo = ({ entity }: EnvironmentInfoProps = {}) => {
  const classes = useStyles();

  if (!entity) {
    return (
      <Card>
        <CardContent>
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              No Environment Selected
            </Typography>
            <Typography variant="body2">
              Click on an environment from the list to view its details
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return <EnvironmentInfoContent entity={entity} />;
};
